import config from "@Config";
import BillingCountries from "@Enums/BillingCountries";
import Currencies from "@Enums/Currencies";
import { CouponResponse, StripeSubscriptionDetails, StripeSubscriptionPrice } from "@Shared/schemas/stripe/stripeSchemas";

export const stripePricingIds = config.stripePricingIds;

export type StripePriceInfo = {
  id: string;
  name: string;
  title: string;
  period: string;
  subtitle: string;
  cta: string;
  pricingId: string;
  color: string;
  productType: string;
  disabled: boolean;
  features: {
    name: string;
    checked: boolean;
  }[];
  membership: string;
  billed?: string;
  savings?: string;
  total?: string;
};

export const monthly: StripePriceInfo = {
  id: stripePricingIds.MONTHLY,
  name: "signup.package.monthly.name",
  title: "signup.package.monthly.title",
  period: "signup.package.monthly.period",
  subtitle: "signup.package.monthly.subtitle",
  cta: "signup.package.monthly.cta",
  pricingId: stripePricingIds.MONTHLY,
  color: "primary",
  productType: "allaccess",
  disabled: true,
  features: [
    {
      name: "signup.feature.1.name",
      checked: true,
    },
    {
      name: "signup.feature.2.name",
      checked: true,
    },
    {
      name: "signup.feature.3.name",
      checked: true,
    },
    {
      name: "signup.feature.4.name",
      checked: true,
    },
    {
      name: "signup.feature.5.name",
      checked: true,
    },
    {
      name: "signup.feature.7.name",
      checked: true,
    },
    {
      name: "signup.feature.13.name",
      checked: true,
    },
    {
      name: "signup.feature.8.name",
      checked: true,
    },
  ],
  membership: "signup.package.monthly.membership",
  billed: "signup.package.monthly.billed",
  total: "signup.package.monthly.total",
};

export const annually: StripePriceInfo = {
  id: stripePricingIds.ANNUALLY,
  name: "signup.package.annual.name",
  title: "signup.package.annual.title",
  period: "signup.package.annual.period",
  subtitle: "signup.package.annual.subtitle",
  cta: "signup.package.annual.cta",
  pricingId: stripePricingIds.ANNUALLY,
  color: "secondary",
  productType: "allaccess",
  disabled: true,
  features: [
    {
      name: "signup.feature.1.name",
      checked: true,
    },
    {
      name: "signup.feature.2.name",
      checked: true,
    },
    {
      name: "signup.feature.3.name",
      checked: true,
    },
    {
      name: "signup.feature.4.name",
      checked: true,
    },
    {
      name: "signup.feature.5.name",
      checked: true,
    },
    {
      name: "signup.feature.7.name",
      checked: true,
    },
    {
      name: "signup.feature.13.name",
      checked: true,
    },
    {
      name: "signup.feature.8.name",
      checked: true,
    },
  ],
  savings: "signup.package.annual.savings",
  membership: "signup.package.annual.membership",
  billed: "signup.package.annual.billed",
  total: "signup.package.annual.total",
};

export const multiSeatMonthly: StripePriceInfo = {
  id: stripePricingIds.MULTI_SEAT_MONTHLY,
  name: "signup.package.multiseat.monthly.name",
  title: "signup.package.multiseat.monthly.title",
  period: "signup.package.multiseat.monthly.period",
  subtitle: "signup.package.multiseat.monthly.subtitle",
  cta: "signup.package.monthly.cta",
  pricingId: stripePricingIds.MULTI_SEAT_MONTHLY,
  color: "secondary",
  productType: "allaccess",
  disabled: true,
  features: [
    {
      name: "signup.feature.1.name",
      checked: true,
    },
    {
      name: "signup.feature.2.name",
      checked: true,
    },
    {
      name: "signup.feature.3.name",
      checked: true,
    },
    {
      name: "signup.feature.4.name",
      checked: true,
    },
    {
      name: "signup.feature.5.name",
      checked: true,
    },
    {
      name: "signup.feature.7.name.multiseat",
      checked: true,
    },
    {
      name: "signup.feature.13.name",
      checked: true,
    },
    {
      name: "signup.feature.8.name",
      checked: true,
    },
  ],
  membership: "signup.package.multiseat.monthly.membership",
  billed: "signup.package.monthly.billed",
};

export const multiSeatAnnually: StripePriceInfo = {
  id: stripePricingIds.MULTI_SEAT_ANNUALLY,
  name: "signup.package.multiseat.annual.name",
  title: "signup.package.multiseat.annual.title",
  period: "signup.package.multiseat.annual.period",
  subtitle: "signup.package.multiseat.annual.subtitle",
  cta: "signup.package.annual.cta",
  pricingId: stripePricingIds.MULTI_SEAT_ANNUALLY,
  color: "secondary",
  productType: "allaccess",
  disabled: true,
  features: [
    {
      name: "signup.feature.1.name",
      checked: true,
    },
    {
      name: "signup.feature.2.name",
      checked: true,
    },
    {
      name: "signup.feature.3.name",
      checked: true,
    },
    {
      name: "signup.feature.4.name",
      checked: true,
    },
    {
      name: "signup.feature.5.name",
      checked: true,
    },
    {
      name: "signup.feature.7.name.multiseat",
      checked: true,
    },
    {
      name: "signup.feature.13.name",
      checked: true,
    },
    {
      name: "signup.feature.8.name",
      checked: true,
    },
  ],
  membership: "signup.package.multiseat.annual.membership",
  billed: "signup.package.annual.billed",
};

export const stripePriceMapping: Record<string, StripePriceInfo> = {
  ...(Boolean(stripePricingIds.MULTI_SEAT_MONTHLY_NOK)
    ? {
        [stripePricingIds.MULTI_SEAT_MONTHLY_NOK]: {
          ...multiSeatMonthly,
          id: stripePricingIds.MULTI_SEAT_MONTHLY_NOK,
          pricingId: stripePricingIds.MULTI_SEAT_MONTHLY_NOK,
        },
      }
    : {}),
  ...(Boolean(stripePricingIds.MULTI_SEAT_ANNUALLY_NOK)
    ? {
        [stripePricingIds.MULTI_SEAT_ANNUALLY_NOK]: {
          ...multiSeatAnnually,
          id: stripePricingIds.MULTI_SEAT_ANNUALLY_NOK,
          pricingId: stripePricingIds.MULTI_SEAT_ANNUALLY_NOK,
        },
      }
    : {}),

  [stripePricingIds.MULTI_SEAT_MONTHLY]: multiSeatMonthly,
  [stripePricingIds.MULTI_SEAT_ANNUALLY]: multiSeatAnnually,
};

// We need this because there are still customers with active subscriptions for prices that are not available anymore (non-tiered pricing)
export const stripeSubscriptionLegacyMapping = {
  ...stripePriceMapping,
  [stripePricingIds.MONTHLY]: monthly,
  [stripePricingIds.ANNUALLY]: annually,
  [stripePricingIds.MULTI_SEAT_MONTHLY]: multiSeatMonthly,
  [stripePricingIds.MULTI_SEAT_ANNUALLY]: multiSeatAnnually,
};

export const DEFAULT_PROFILE_ALLOWANCE = 3;
export const DEFAULT_TIERED_MIN = 3;
export const DEFAULT_TIERED_MAX = 9;

export const stripeSubscriptionStorageKey = "subscription_type";

export const currencyMapping: Record<Currencies, string> = {
  [Currencies.USD]: "$",
  [Currencies.EUR]: "€",
  [Currencies.GBP]: "£",
  [Currencies.AUD]: "$",
  [Currencies.CAD]: "$",
  [Currencies.NZD]: "$",
  [Currencies.CHF]: "CHF",
  [Currencies.SEK]: "kr",
  [Currencies.DKK]: "kr",
  [Currencies.NOK]: "kr",
};

const isKnownCurrency = (currency: string): currency is Currencies => {
  return Object.prototype.hasOwnProperty.call(currencyMapping, currency);
};

export function getCurrencySymbol(currency: string): string {
  return isKnownCurrency(currency) ? currencyMapping[currency] : currency.toUpperCase();
}

const decimalCurrencies: ReadonlySet<string> = new Set<Currencies>([
  Currencies.USD,
  Currencies.EUR,
  Currencies.GBP,
  Currencies.SEK,
  Currencies.DKK,
  Currencies.NOK,
]);

interface IExclusivePromoCodes {
  pricingId: string;
  errorMessage: string;
}
export const exclusivePromoCodes = new Map<string, IExclusivePromoCodes>([
  [
    "gca-",
    {
      pricingId: stripePricingIds.MULTI_SEAT_ANNUALLY,
      errorMessage: "checkout.subscription.promo.exlusive.error.gca",
    },
  ],
]);

export const getFlatPrice = (price: StripeSubscriptionPrice) => {
  if (!price) return 0;
  let amount = price.unit_amount || 0;
  if (price.tiers) {
    const priceWithFlatAmount = price.tiers?.find(t => t.flat_amount || +(t.flat_amount_decimal || 0));
    if (priceWithFlatAmount) {
      amount = priceWithFlatAmount.flat_amount || +(priceWithFlatAmount.flat_amount_decimal || 0);
    }
  }
  return amount / 100;
};

export const getMonthlyPrice = (price: StripeSubscriptionPrice) => {
  const amount = getFlatPrice(price);

  if (price.type === "recurring") {
    if (price.recurring?.interval === "year") {
      return round(amount / 12);
    }
  }
  return round(amount);
};

export const getDisplayPrice = (amount: number, currency: string) => {
  return round(decimalCurrencies.has(currency) ? amount / 100 : amount);
};

export const formatPrice = (amount: number, currency: string) => {
  const indicator = amount >= 0 ? "" : "-";
  return `${indicator}${getCurrencySymbol(currency)}${Math.abs(amount).toFixed(2)} ${currency.toUpperCase()}`;
};

export const getDisplayPriceWithUnit = (amount: number, currency: string) => {
  const price = getDisplayPrice(amount || 0, currency);
  return formatPrice(price, currency);
};

export const getDisplayPriceForSubscription = (price: StripeSubscriptionPrice, withUnit: boolean = true) => {
  if (!price) return "";
  const priceTiers = getTieredPricing(price);
  const monthlyPrice = getMonthlyPrice(price);

  // For SubscriptionPrice type
  if (!priceTiers) {
    return withUnit ? getDisplayPriceWithUnit(price.unit_amount || 0, price.currency) : getDisplayPrice(price.unit_amount || 0, price.currency);
  }

  // For Price type
  return `${getCurrencySymbol(price.currency)}${monthlyPrice} ${withUnit ? price.currency.toUpperCase() : ""}`;
};

type TieredPricing = {
  flat_amount: number | null;
  unit_amount: number | null;
  min: number | null;
  max: number | null;
};
export const getTieredPricing = (price: StripeSubscriptionPrice): TieredPricing | null => {
  if (!price.tiers) return null;
  let flat_amount: number | null = 0;
  let unit_amount: number | null = 0;
  let min: number | null = 0;
  let max: number | null = 100;
  for (const tier of price.tiers) {
    if (tier.flat_amount) {
      flat_amount = tier.flat_amount || 0;
      min = tier.up_to || 1;
    } else if (tier.unit_amount) {
      unit_amount = tier.unit_amount;
      max = tier.up_to;
    } else if (tier.unit_amount_decimal) {
      unit_amount = +tier.unit_amount_decimal;
      max = tier.up_to;
    }
  }
  return {
    flat_amount,
    unit_amount,
    min,
    max,
  };
};

const round = (num: number) => {
  return num % 1 > 0 ? Number(num.toFixed(2)) : num;
};

export const getInterval = (price: StripeSubscriptionPrice | null | undefined) => {
  if (!price) return undefined;
  if ("interval" in price && price.interval) return price.interval;
  if ("recurring" in price && price.recurring?.interval) return price.recurring.interval;
  return undefined;
};

export const getSubscriptionPeriodEnd = (subscription: StripeSubscriptionDetails): number | null =>
  subscription.items.data[0]?.current_period_end ?? null;

export const getCouponAnalyticsPayload = (subscription: StripeSubscriptionDetails, coupon: CouponResponse) => {
  const { currency, total_discount_amounts } = subscription.latest_invoice;
  const discountAmount = total_discount_amounts?.reduce((acc, discount) => acc + discount.amount, 0) || 0;

  return {
    discount_amount: discountAmount / 100,
    price_id: subscription.items.data[0].price.id,
    coupon_id: coupon.id,
    subscription_id: subscription.id,
    coupon_promo_id: coupon.promoId,
    coupon_promo_name: coupon.promoName,
    coupon_duration: coupon.duration,
    coupon_discount: Math.round((1 - (coupon.discount || 1)) * 100) / 100, // 1 - discount = discount percentage. I unfortunately cooked up some weird shit on the backend for this field.
    coupon_amount_off: coupon.amountOff || 0,
    currency,
  };
};

type CheckoutPrice = {
  id: string;
  currency: string;
  unit_amount: number | null;
  tiers?: unknown;
  tiers_mode?: string | null;
  currency_options?: Record<string, unknown>;
};

export function resolveCheckoutPrice<T extends CheckoutPrice>(price: T, country: string, prices: T[], nokPriceId?: string): T | undefined {
  if (country.toUpperCase() !== BillingCountries.NORWAY || price.currency === Currencies.NOK) return price;
  const nok = price.currency_options?.[Currencies.NOK] as { unit_amount: number | null; tiers?: unknown } | undefined;
  if (nok && (nok.unit_amount != null || Array.isArray(nok.tiers))) {
    return { ...price, currency: Currencies.NOK, unit_amount: nok.unit_amount, tiers: nok.tiers };
  }
  return prices.find(candidate => candidate.id === nokPriceId && candidate.currency === Currencies.NOK);
}

export function checkoutAmount(price: CheckoutPrice, quantity: number): number {
  if (!Array.isArray(price.tiers)) return (price.unit_amount || 0) * quantity;
  let amount = 0;
  let previousLimit = 0;
  for (const tier of price.tiers) {
    const limit = tier.up_to ?? Infinity;
    const unit = Number(tier.unit_amount_decimal ?? tier.unit_amount ?? 0);
    const flat = Number(tier.flat_amount_decimal ?? tier.flat_amount ?? 0);
    if (price.tiers_mode === "volume") {
      if (quantity <= limit) return quantity * unit + flat;
    } else {
      amount += Math.max(0, Math.min(quantity, limit) - previousLimit) * unit + flat;
      if (quantity <= limit) return amount;
    }
    previousLimit = limit;
  }
  return amount;
}
